import React, { useState } from 'react';
import { Info, Check, X, Maximize2 } from 'lucide-react';
import type { NormalizedQuestion } from '../lib/types';
import { translations, formatString } from '../lib/i18n';
import { useData } from '../context/DataContext';

interface QuizQuestionProps {
  question: NormalizedQuestion;
  currentIndex: number;
  totalQuestions: number;
  selectedIndices: number[];
  isSubmitted: boolean;
  facultyName?: string;
  lessonName?: string;
  onToggleOption: (index: number) => void;
  onValidate: () => void;
}

export const QuizQuestion: React.FC<QuizQuestionProps> = ({
  question,
  currentIndex,
  totalQuestions,
  selectedIndices,
  isSubmitted,
  facultyName,
  lessonName,
  onToggleOption,
  onValidate,
}) => {
  const { language } = useData();
  const t = translations[language];

  const [isImageZoomed, setIsImageZoomed] = useState(false);

  const isMultiple = question.max_selections > 1 || question.answer_indices.length > 1;

  return (
    <div className="compact-quiz-container">
      {/* COMPACT QUESTION CARD */}
      <article className="compact-question-card" aria-label="Question">
        {/* Integrated Slim Meta Bar inside the card header */}
        <div className="compact-meta-bar">
          <div className="compact-meta-chips">
            <span className="compact-chip active">
              {formatString(t.questionOfTotal, {
                current: currentIndex + 1,
                total: totalQuestions,
              })}
            </span>

            {question.year && (
              <span className="compact-chip">
                {t.session} {question.year}
              </span>
            )}

            {question.state && (
              <span className="compact-chip">
                {question.state}
              </span>
            )}

            {question.chapter && (
              <span className="compact-chip">
                {question.chapter}
              </span>
            )}

            <span className="compact-chip mode-chip">
              {isMultiple
                ? formatString(t.chooseNAnswers, { n: question.max_selections })
                : t.singleChoiceOnly}
            </span>
          </div>

          {lessonName && (
            <span className="compact-lesson-badge" title={lessonName} dir="auto">
              {lessonName}
            </span>
          )}
        </div>

        {/* Question Text */}
        <h2 className="compact-question-text" dir="auto">
          {question.question_text}
        </h2>

        {/* Optional Image with Zoom feature */}
        {question.image_url && (
          <div className="compact-media-wrap">
            <img
              src={question.image_url}
              alt="Question illustration"
              className="compact-question-img"
              loading="lazy"
              referrerPolicy="no-referrer"
              onClick={() => setIsImageZoomed(true)}
            />
            <button
              type="button"
              className="compact-zoom-btn"
              onClick={() => setIsImageZoomed(true)}
              title="Agrandir l'image"
            >
              <Maximize2 size={13} />
            </button>
          </div>
        )}
      </article>

      {/* Image Zoom Modal */}
      {isImageZoomed && question.image_url && (
        <div className="image-zoom-overlay" onClick={() => setIsImageZoomed(false)}>
          <div className="image-zoom-box" onClick={(e) => e.stopPropagation()}>
            <img
              src={question.image_url}
              alt="Zoomed question illustration"
              className="image-zoom-full"
              referrerPolicy="no-referrer"
            />
            <button
              type="button"
              className="image-zoom-close"
              onClick={() => setIsImageZoomed(false)}
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* OPTIONS STACK (COMPACT, VIEW-FIT) */}
      <div
        className="compact-options-stack"
        role="group"
        aria-label="Options"
        aria-live="polite"
      >
        {question.options.map((opt) => {
          const isSelected = selectedIndices.includes(opt.index);
          const isCorrect = question.answer_indices.includes(opt.index);

          let optionStateClass = '';
          let feedbackLabel = null;

          if (isSubmitted) {
            optionStateClass = 'locked ';
            if (isSelected && !isCorrect) {
              optionStateClass += 'feedback-picked-wrong';
              feedbackLabel = (
                <span className="compact-feedback-tag wrong">
                  <X size={13} /> {t.incorrectAnswer}
                </span>
              );
            } else if (isSelected && isCorrect) {
              optionStateClass += 'feedback-picked-correct';
              feedbackLabel = (
                <span className="compact-feedback-tag correct">
                  <Check size={13} /> {t.correctAnswer}
                </span>
              );
            } else if (!isSelected && isCorrect) {
              optionStateClass += 'feedback-correct-unpicked';
              feedbackLabel = (
                <span className="compact-feedback-tag correct">
                  {t.theCorrectAnswer}
                </span>
              );
            } else {
              optionStateClass += 'feedback-dimmed';
            }
          } else {
            if (isSelected) {
              optionStateClass = 'selected-unanswered';
            }
          }

          return (
            <button
              key={opt.index}
              type="button"
              className={`compact-option-row ${optionStateClass}`}
              onClick={() => {
                if (!isSubmitted) {
                  onToggleOption(opt.index);
                }
              }}
              disabled={isSubmitted}
              aria-pressed={isSelected}
            >
              <div className="compact-option-content">
                <span className="compact-letter-badge" aria-hidden="true">
                  {opt.letter}
                </span>
                <span className="compact-option-text" dir="auto">
                  {opt.text}
                </span>
              </div>

              {feedbackLabel}
            </button>
          );
        })}
      </div>

      {/* MULTIPLE CHOICE VALIDATE BUTTON */}
      {isMultiple && !isSubmitted && (
        <div className="compact-validate-container">
          <button
            type="button"
            className="btn-primary compact-validate-btn"
            onClick={onValidate}
            disabled={selectedIndices.length === 0}
          >
            <Check size={16} /> {t.validate}
          </button>
        </div>
      )}

      {/* EXPLANATION BOX */}
      {isSubmitted && question.explanation && question.explanation.trim() && (
        <div className="compact-explanation-box" role="region" aria-label={t.explanation}>
          <div className="explanation-title">
            <Info size={16} aria-hidden="true" />
            <span>{t.explanation}</span>
          </div>
          <div className="explanation-text" dir="auto">
            {question.explanation}
          </div>
        </div>
      )}
    </div>
  );
};
